import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Card Component (No changes needed here)
const Card = ({ number, title, subtitle, description, listItems, image, buttonText, link, cardRef, listRef }) => (
    <section className="py-4 md:py-8 perspective-1000" ref={cardRef}>
        <div className="container mx-auto bg-white p-8 rounded-lg shadow-lg transform-gpu preserve-3d hover:shadow-xl transition-shadow duration-300">
            <div className="flex flex-col md:flex-row items-stretch">
                <div className="md:w-1/6 flex md:items-end justify-center pb-6 md:pb-6 mb-4 md:mb-0">
                    <div className="text-gray-200 flex items-center justify-center md:mr-4 text-6xl md:text-8xl font-bold card-number">
                        {number}
                    </div>
                </div>
                <div className="md:w-5/6 md:pr-8">
                    <h3 className="text-2xl md:text-3xl lg:text-4xl leading-tight mb-2 card-title">
                        {title}
                        <br />
                        <span className="block font-semibold mt-1 text-blue-600 card-subtitle">{subtitle}</span>
                    </h3>
                    <div className="pt-4 card-hr">
                        <hr className="border-blue-100" />
                        <p className="text-md font-medium text-gray-600 mt-2 card-description">{description}</p>
                    </div>
                    <ul className="space-y-2 text-md text-gray-700 mt-4 md:mt-4 card-list" ref={listRef}>
                        {listItems.map((item, index) => (
                            <li key={index} className="flex items-start transform-gpu card-list-item">
                                <svg className="h-5 w-5 md:h-6 md:w-6 text-blue-600 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span><span className="font-medium">{item.title}</span>{item.description}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="md:w-1/3 mt-8 md:mt-0 flex flex-col items-center card-image-container">
                    <img src={image} alt={`${title} graphic`} className="w-full h-auto mb-4 md:mb-6 rounded-lg card-image" />
                    <a href={link} className="card-button-link">
                        <button className="bg-blue-600 text-white font-medium text-sm md:text-md py-2 px-6 rounded-full hover:bg-blue-700 transition-colors duration-300 transform hover:scale-105 card-button">
                            {buttonText}
                        </button>
                    </a>
                </div>
            </div>
        </div>
    </section>
);

// New Animation Utility
const createSmoothEntranceAnimation = (card, index) => {
    const cardElements = {
        card: card,
        number: card.querySelector('.card-number'),
        title: card.querySelector('.card-title'),
        subtitle: card.querySelector('.card-subtitle'),
        hr: card.querySelector('.card-hr'),
        description: card.querySelector('.card-description'),
        list: card.querySelector('.card-list'),
        listItems: card.querySelectorAll('.card-list-item'),
        imageContainer: card.querySelector('.card-image-container'),
        image: card.querySelector('.card-image'),
        buttonLink: card.querySelector('.card-button-link'),
        button: card.querySelector('.card-button')
    };

    gsap.set(cardElements.card, { yPercent: 20, opacity: 0 }); 

    const tl = gsap.timeline({
        scrollTrigger: {
            trigger: cardElements.card,
            start: "top 80%",
            end: "bottom bottom",
            once: true,
        }
    });

    tl.to(cardElements.card, { yPercent: 0, opacity: 1, duration: 0.8, ease: "power2.out", delay: index * 0.1 }) // Slide up and fade in card
        .fromTo(cardElements.number, { opacity: 0, x: -20 }, { opacity: 1, x: 0, duration: 0.4, ease: "power2.out" }, "-=0.5") // Animate number
        .fromTo(cardElements.title, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, "-=0.3") // Animate title
        .fromTo(cardElements.subtitle, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, "-=0.3") // Animate subtitle
        .fromTo(cardElements.hr, { opacity: 0, scaleX: 0 }, { opacity: 1, scaleX: 1, duration: 0.6, ease: "power3.out" }, "-=0.2") // Animate hr
        .fromTo(cardElements.description, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, "-=0.3") // Animate description
        .fromTo(cardElements.listItems, { opacity: 0, y: 10, stagger: 0.1 }, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out", stagger: 0.1 }, "-=0.2") // Animate list items
        .fromTo(cardElements.imageContainer, { opacity: 0, x: 20 }, { opacity: 1, x: 0, duration: 0.5, ease: "power2.out" }, "-=0.4") // Animate image container
        .fromTo(cardElements.buttonLink, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, "-=0.3"); // Animate button link
};


// Main Component
const WhatWeDo = () => {
    const sectionRef = useRef(null);
    const cardsRef = useRef([]);
    const listsRef = useRef([]);

    const addToRefs = (refArray) => (el) => {
        if (el && !refArray.current.includes(el)) {
            refArray.current.push(el);
        }
    };

    const addToCardsRef = addToRefs(cardsRef);
    const addToListsRef = addToRefs(listsRef);

    useEffect(() => {

        gsap.fromTo(
            sectionRef.current,
            { opacity: 0, y: 20 }, 
            { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
        );

        cardsRef.current.forEach((card, index) => {
            createSmoothEntranceAnimation(card, index); 
        });

        const handleResize = () => {
            ScrollTrigger.refresh();
        };

        window.addEventListener("resize", handleResize);

        return () => {
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const cardData = [
        {
            number: 1,
            title: "For Global Startups",
            subtitle: "Expand in India with Confidence",
            description: "Seamless Market Entry & Growth in India",
            listItems: [
                { title: "Market Research & Localization: ", description: "Understanding cultural & business dynamics." },
                { title: "Regulatory & Compliance Support: ", description: "Navigating Indian legal frameworks." },
                { title: "Sales & Distribution Partnerships: ", description: "Connecting with the right industry players." },
                { title: "Investor & Funding Access: ", description: "Raising capital through our ecosystem." }
            ],
            image: "img/global.png",
            buttonText: "Expand in India",
            link: "/global"
        },
        {
            number: 2,
            title: "For Indian Startups",
            subtitle: "Accelerate Growth Across Asia",
            description: "From Idea to Market Leader",
            listItems: [
                { title: "Product-Market Fit & GTM Execution: ", description: "Validating and scaling your product." },
                { title: "Sales Acceleration & Enterprise Partnerships: ", description: "Connecting with customers & enterprises." },
                { title: "Operational Scale-Up: ", description: "Optimizing processes for rapid expansion." },
                { title: "Fundraising & Investor Access: ", description: "Connecting with strategic investors." }
            ],
            image: "img/india.png",
            buttonText: "Accelerate Growth",
            link: "/indian"
        },
        {
            number: 3,
            title: "For Enterprises",
            subtitle: "Innovate Through Startups",
            description: "Enterprise-Startup Collaboration for Disruptive Innovation",
            listItems: [
                { title: "Identify ", description: "business challenges." },
                { title: "Curate ", description: "high-potential startup solutions." },
                { title: "Pilot & Validate ", description: "through strategic collaborations." },
                { title: "Scale & Integrate ", description: "innovation seamlessly." }
            ],
            image: "img/enterprise.png",
            buttonText: "Partner with Startups",
            link: "/enterprise"
        }
    ];

    return (
        <div className="py-12 overflow-hidden" ref={sectionRef}>
            <div className="max-w-7xl mx-auto md:px-6">
                {cardData.map((card, index) => (
                    <Card
                        key={index}
                        {...card}
                        cardRef={addToCardsRef}
                        listRef={addToListsRef}
                    />
                ))}
            </div>
        </div>
    );
};

export default WhatWeDo;